import { useState, useEffect } from 'react';
import { Search, Play, Tv, Star } from 'lucide-react';
import { streamFlowApi } from '@lib/streamFlowApi';
import type { Channel, Household } from '@lib/streamFlowApi';
import { useRovingFocus } from '@hooks/useRovingFocus';

// Tailwind responsive grid: 2 cols mobile → 6 cols xl. We pass the
// best-guess column count to useRovingFocus so D-pad up/down jumps
// rows by the right amount. Recomputed on resize via a small effect.
function useGridColumns(): number {
  const [cols, setCols] = useState(() => columnsForViewport(typeof window !== 'undefined' ? window.innerWidth : 1024));
  useEffect(() => {
    const onResize = () => setCols(columnsForViewport(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return cols;
}

function columnsForViewport(w: number): number {
  if (w >= 1280) return 6; // xl
  if (w >= 1024) return 5; // lg
  if (w >= 768)  return 4; // md
  if (w >= 640)  return 3; // sm
  return 2;
}

interface CategoryOption {
  name: string;
  count: number;
}

interface ChannelGridProps {
  /** Initial channels passed in by the dashboard (first ~100). The grid
   * takes over fetching as soon as the user types a search query or
   * picks a category — those queries hit /api/guide/channels with
   * server-side filters and pagination instead of slicing this prop. */
  channels: Channel[];
  /** Either the new shape {name, count} from /api/guide/categories or
   * the legacy `string[]` for backward compat. */
  categories: CategoryOption[] | string[];
  onPlay: (channel: Channel) => void;
  loading?: boolean;
  /** When set, channel cards show a star toggle that writes to sf_favorites
   * for this family member. When unset, the star is hidden. */
  currentMemberId?: string | null;
}

const PAGE_SIZE = 100;

// Display labels for canonical category buckets.
const CANONICAL_LABELS: Record<string, string> = {
  sports: '🏈 Sports',
  news: '📰 News',
  movies: '🎬 Movies',
  series: '📺 Series',
  kids: '🧒 Kids',
  music: '🎵 Music',
  lifestyle: '🌿 Lifestyle',
  international: '🌎 International',
  religious: '🕊 Religious',
  adult: '🔞 Adult',
  local: '📍 Local',
  other: '✨ Other',
};

// Region-aware category row returned by /api/guide/categories?detail=region.
interface RegionCategory {
  key: string;        // 'us:news' | '247:anime' | …
  label: string;      // '🇺🇸 USA News' | '📺 24/7 — Anime'
  bucket: string | null;
  region: string | null;
  count: number;
}

export default function ChannelGrid({ channels: initialChannels, categories, onPlay, loading: parentLoading, currentMemberId }: ChannelGridProps) {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const columns = useGridColumns();

  // Region-aware categories from the /detail=region endpoint. Used to
  // render the chip rows split by (primary region) vs (other regions)
  // vs (24/7 + region-less). Falls back to the prop-passed `categories`
  // if the fetch fails (zero-regression on backend rollback).
  const [regionCategories, setRegionCategories] = useState<RegionCategory[]>([]);
  const [primaryRegion, setPrimaryRegion] = useState<string>('us');
  const [showMoreRegions, setShowMoreRegions] = useState(false);
  useEffect(() => {
    streamFlowApi.listCategoriesByRegion().then((res) => {
      if (res.success && res.data) setRegionCategories(res.data);
    });
    streamFlowApi.listHouseholds().then((res) => {
      if (!res.success || !res.data || res.data.length === 0) return;
      // Use the first household's primary_region. Multi-household users
      // can switch households via the header switcher; this re-renders
      // when activeHousehold changes via HouseholdContext (not wired
      // here yet — minor follow-up).
      const h = res.data[0] as Household & { primary_region?: string };
      if (h.primary_region) setPrimaryRegion(h.primary_region);
    });
  }, []);

  // Server-driven channel list. Initialized from the parent's pre-fetched
  // sample (first 100) so the dashboard still has something to render
  // on mount; once the user types a search or picks a category, this
  // component starts driving the queries with server-side filters +
  // pagination so we're not slicing 23k channels client-side.
  const [serverChannels, setServerChannels] = useState<Channel[]>(initialChannels);
  const [total, setTotal] = useState<number>(initialChannels.length);
  const [page, setPage] = useState(1);
  const [fetching, setFetching] = useState(false);
  // Keep the local list in sync if the parent prop changes (e.g. after
  // a manual refresh) AS LONG AS the user hasn't started filtering.
  useEffect(() => {
    if (!search && !selectedCategory && page === 1) {
      setServerChannels(initialChannels);
      setTotal(initialChannels.length);
    }
  }, [initialChannels, search, selectedCategory, page]);

  // Debounced server-side query on filter change. ~300ms keeps typing
  // smooth without firing a request per keystroke.
  useEffect(() => {
    if (!search && !selectedCategory) {
      // User cleared all filters — revert to parent's initial sample
      // rather than leaving the previous filtered result on screen.
      setServerChannels(initialChannels);
      setTotal(initialChannels.length);
      setPage(1);
      return;
    }

    const t = setTimeout(async () => {
      setFetching(true);
      const res = await streamFlowApi.listChannels({
        search: search || undefined,
        category: selectedCategory ?? undefined,
        page: 1,
        limit: PAGE_SIZE,
      });
      setFetching(false);
      if (res.success && res.data) {
        setServerChannels(res.data);
        setTotal(res.pagination?.total ?? res.data.length);
        setPage(1);
      }
    }, 300);
    return () => clearTimeout(t);
    // initialChannels intentionally excluded — we don't want a parent
    // refresh during filtering to overwrite the user's filtered view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, selectedCategory]);

  const loadMore = async () => {
    const next = page + 1;
    setFetching(true);
    const res = await streamFlowApi.listChannels({
      search: search || undefined,
      category: selectedCategory ?? undefined,
      page: next,
      limit: PAGE_SIZE,
    });
    setFetching(false);
    if (res.success && res.data) {
      setServerChannels((prev) => [...prev, ...(res.data ?? [])]);
      setPage(next);
    }
  };

  // Load this member's favorites whenever the active profile changes. Cheap
  // query (RLS-scoped) so refreshing on every switch is fine.
  useEffect(() => {
    if (!currentMemberId) {
      setFavoriteIds(new Set());
      return;
    }
    let cancelled = false;
    streamFlowApi.listFavorites(currentMemberId).then((res) => {
      if (cancelled || !res.success || !res.data) return;
      setFavoriteIds(new Set(res.data.map((f) => f.channel_id)));
    });
    return () => { cancelled = true; };
  }, [currentMemberId]);

  const toggleFavorite = async (channelId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentMemberId) return;
    // Optimistic update — matches the snappy feel of the rest of the dashboard.
    const isFav = favoriteIds.has(channelId);
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (isFav) next.delete(channelId);
      else next.add(channelId);
      return next;
    });
    const res = isFav
      ? await streamFlowApi.removeFavorite(currentMemberId, channelId)
      : await streamFlowApi.addFavorite(currentMemberId, channelId);
    if (!res.success) {
      // Roll back on failure.
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (isFav) next.add(channelId);
        else next.delete(channelId);
        return next;
      });
    }
  };

  // Normalize categories to {name,count} shape regardless of input form.
  const categoryOptions: CategoryOption[] = Array.isArray(categories) && categories.length > 0
    ? typeof categories[0] === 'string'
      ? (categories as string[]).map(name => ({ name, count: 0 }))
      : (categories as CategoryOption[])
    : [];

  // Server returned the channels already filtered. We just keep the
  // 24/7 pinning so live channels stay up top. (Stable sort means the
  // server's ordering inside each bucket is preserved.)
  const filtered = [...serverChannels].sort((a, b) => {
    const a247 = a.is_24_7_channel ? 1 : 0;
    const b247 = b.is_24_7_channel ? 1 : 0;
    return b247 - a247;
  });
  const hasMore = serverChannels.length < total;
  const loading = parentLoading || fetching;

  return (
    <div>
      {/* Search + Category chips */}
      <div className="flex flex-col gap-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search channels..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
          />
        </div>
        {/* When the region-aware endpoint has data, render the new
            grouped chips: primary region first, then 24/7, then a
            "More regions" expander. Falls back to the legacy bucket
            chips if the new endpoint returned empty (e.g. backend
            rolled back). */}
        {regionCategories.length > 0 ? (
          <RegionChipGroups
            categories={regionCategories}
            primaryRegion={primaryRegion}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            showMoreRegions={showMoreRegions}
            setShowMoreRegions={setShowMoreRegions}
          />
        ) : (
          categoryOptions.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setSelectedCategory(null)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  selectedCategory === null
                    ? 'border-slate-800 bg-slate-800 text-white'
                    : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                All
              </button>
              {categoryOptions.map(({ name, count }) => {
                const label = CANONICAL_LABELS[name] ?? name;
                const selected = selectedCategory === name;
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setSelectedCategory(selected ? null : name)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      selected
                        ? 'border-slate-800 bg-slate-800 text-white'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    {label}
                    {count > 0 && (
                      <span className={`ml-1.5 ${selected ? 'opacity-70' : 'text-gray-400'}`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )
        )}
      </div>

      {/* Result count */}
      {!loading && (search || selectedCategory) && (
        <p className="text-xs text-gray-500 mb-2">
          {total === 0
            ? 'No channels match'
            : `Showing ${filtered.length} of ${total.toLocaleString()} channels`}
        </p>
      )}

      {/* Channel Grid */}
      {loading && filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">Loading channels...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {search || selectedCategory ? 'No channels match your filters' : 'No channels available. Add a provider first.'}
        </div>
      ) : (
        <>
          <ChannelTiles
            filtered={filtered}
            columns={columns}
            onPlay={onPlay}
            currentMemberId={currentMemberId}
            favoriteIds={favoriteIds}
            toggleFavorite={toggleFavorite}
          />
          {hasMore && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={fetching}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                {fetching ? 'Loading…' : `Load ${Math.min(PAGE_SIZE, total - filtered.length)} more`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Region-aware chip group renderer. Splits the category list into:
 *   1. Primary-region chips (household's `primary_region`)
 *   2. 24/7 chips (region-null, bucket=247 — own row because Tivimate
 *      users expect these as siblings to the main buckets, not nested)
 *   3. "More regions" expander — everything else, sorted by count desc
 *
 * Each chip filters channels by `category_display_key`, which the
 * /api/guide/channels endpoint matches against the channel row's
 * stored display_key. Selecting a chip swaps in a fully different
 * server-side query (debounced from search/category state in the
 * parent), so 24/7 Anime shows 72 channels and nothing else.
 */
function RegionChipGroups({
  categories,
  primaryRegion,
  selectedCategory,
  setSelectedCategory,
  showMoreRegions,
  setShowMoreRegions,
}: {
  categories: RegionCategory[];
  primaryRegion: string;
  selectedCategory: string | null;
  setSelectedCategory: (k: string | null) => void;
  showMoreRegions: boolean;
  setShowMoreRegions: (v: boolean) => void;
}) {
  const primary = categories.filter((c) => c.region === primaryRegion);
  const t247 = categories.filter((c) => c.bucket === '247');
  // Exclude both groups above + region-null non-247 (the "International"
  // catch-all bucket gets pushed into the More expander).
  const more = categories.filter(
    (c) => c.region !== primaryRegion && c.bucket !== '247',
  );

  return (
    <div className="flex flex-col gap-3">
      {/* All */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setSelectedCategory(null)}
          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
            selectedCategory === null
              ? 'border-slate-800 bg-slate-800 text-white'
              : 'border-gray-200 bg-white hover:bg-gray-50'
          }`}
        >
          All
        </button>
        {primary.map((c) => (
          <Chip key={c.key} category={c} selected={selectedCategory === c.key} onClick={() => setSelectedCategory(selectedCategory === c.key ? null : c.key)} />
        ))}
      </div>

      {t247.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="text-[10px] uppercase tracking-wide text-gray-500 self-center mr-1">24/7</span>
          {t247.map((c) => (
            <Chip key={c.key} category={c} selected={selectedCategory === c.key} onClick={() => setSelectedCategory(selectedCategory === c.key ? null : c.key)} />
          ))}
        </div>
      )}

      {more.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowMoreRegions(!showMoreRegions)}
            className="text-[11px] text-blue-600 hover:underline mb-1.5"
          >
            {showMoreRegions ? '▼ Hide other regions' : `▸ More regions (${more.length})`}
          </button>
          {showMoreRegions && (
            <div className="flex flex-wrap gap-2">
              {more.map((c) => (
                <Chip key={c.key} category={c} selected={selectedCategory === c.key} onClick={() => setSelectedCategory(selectedCategory === c.key ? null : c.key)} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Chip({ category, selected, onClick }: { category: RegionCategory; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
        selected
          ? 'border-slate-800 bg-slate-800 text-white'
          : 'border-gray-200 bg-white hover:bg-gray-50'
      }`}
    >
      {category.label}
      {category.count > 0 && (
        <span className={`ml-1.5 ${selected ? 'opacity-70' : 'text-gray-400'}`}>
          {category.count}
        </span>
      )}
    </button>
  );
}

/**
 * Tile rendering split out so the roving-focus hook only runs when
 * we have a non-empty grid (avoids a count-of-zero edge case + keeps
 * the parent component cleaner).
 */
function ChannelTiles({
  filtered,
  columns,
  onPlay,
  currentMemberId,
  favoriteIds,
  toggleFavorite,
}: {
  filtered: Channel[];
  columns: number;
  onPlay: (c: Channel) => void;
  currentMemberId?: string | null;
  favoriteIds: Set<string>;
  toggleFavorite: (channelId: string, e: React.MouseEvent) => void;
}) {
  const grid = useRovingFocus({ count: filtered.length, columns });
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3"
      role="grid"
      onKeyDown={grid.onKeyDown}
    >
      {filtered.map((channel, i) => (
        <button
          key={channel.id}
          ref={(el) => { grid.itemRefs.current[i] = el; }}
          tabIndex={grid.activeIndex === i ? 0 : -1}
          onFocus={() => grid.setActiveIndex(i)}
          onClick={() => onPlay(channel)}
          className="group border rounded-lg p-3 text-left hover:border-slate-500 hover:shadow-md transition-all"
        >
          <div className="w-full aspect-video bg-gray-100 rounded mb-2 flex items-center justify-center overflow-hidden relative">
            {channel.logo_url ? (
              <img
                src={channel.logo_url}
                alt={channel.name}
                className="w-full h-full object-contain"
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <Tv className="w-8 h-8 text-gray-300" />
            )}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
              <Play className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            {currentMemberId && (
              <button
                onClick={(e) => toggleFavorite(channel.id, e)}
                aria-label={favoriteIds.has(channel.id) ? 'Unfavorite' : 'Favorite'}
                className="absolute top-1 right-1 p-1 rounded-full bg-black/40 hover:bg-black/70 transition-colors"
              >
                <Star className={`w-4 h-4 ${favoriteIds.has(channel.id) ? 'text-yellow-400 fill-yellow-400' : 'text-white'}`} />
              </button>
            )}
          </div>
          <p className="text-xs font-medium truncate">{channel.name}</p>
          {channel.category && (
            <p className="text-xs text-gray-400 truncate">{channel.category}</p>
          )}
          <div className="flex flex-wrap items-center gap-1 mt-1">
            {channel.is_24_7_channel && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-red-600" />
                </span>
                Live
              </span>
            )}
            {channel.sf_channel_sources && channel.sf_channel_sources.length > 1 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                {channel.sf_channel_sources.length} mirrors
              </span>
            )}
          </div>
        </button>
      ))}
    </div>
  );
}
